import React, { useEffect, useState, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faQuestion,
  faExternalLinkAlt,
  faTableCells,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router-dom";
import DraftModal from "./DraftModal";
import { getDvpColor, rankColor } from "./dvpColor";
import {
  orderLeagues,
  nextLeagueSort,
  isDynastyLeague,
  NO_LEAGUE_SORT,
} from "./leagueOrder";
import { projectedPoints } from "./leagueScoring";
import {
  seasonStatsUrl,
  scoringStatKeys,
  reduceStatRows,
  averageFptsPerGame,
} from "./playerForm";
import { getCurrentWeek } from "./currentWeekCache";
import { fetchMatchups, startersFor } from "./sleeperLineup";
import { loadHiddenLeagues, visibleLeagues } from "./leagueVisibility";
import "./LeagueList.css";
import "./TableStyles.css";
import PositionPill from "./PositionPill";
import { emptyBenchSlots } from "./rosterSlots";
import LeagueIssues from "./LeagueIssues";
import { findIrIssues, findIrReturnIssues } from "./irIssues";
import { useSleeperAuth } from "./auth";
import { positionClass } from "./positionColors";
import { resolvePlayerSearch, leaguesWithPlayer, playerKey } from "./playerSearch";

// The two Sleeper calls in this file have to agree about the season.
const SEASON = 2026;

// Add a mock flag
const mock = process.env.REACT_APP_MOCK === 'true';

// Define the base URL based on the mock flag
const BASE_URL = mock
  ? "http://localhost:5000"
  : "https://shaggy-latashia-carnade-2ea2054a.koyeb.app";

// Sleeper's per-league `leg` cannot decide what "this week" means: the flip is not
// synchronised across leagues (on 16 Sep some still stamped leg 1 on claims entered
// that morning while others had moved to leg 2), and a dynasty league's offseason
// claims all sit at leg 1 too. The NFL week turns over on Tuesday, so that is the
// cutoff — and a claim that has not been processed yet is current whenever it was
// entered, since it is still waiting to run.
function currentWaiverPeriodStart(now = new Date()) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const daysSinceTuesday = (start.getDay() - 2 + 7) % 7; // 0 = Sunday, 2 = Tuesday
  start.setDate(start.getDate() - daysSinceTuesday);
  return start.getTime();
}

function waiverIsProcessed(t) {
  return t.status === "complete" || t.status === "failed";
}

function waiverIsCurrent(t, periodStart) {
  return !waiverIsProcessed(t) || (t.created ?? 0) >= periodStart;
}

// League Name, Record, Pos, MPF, Waivers, Empty bench, Injuries, Actions.
// Used by the
// group headings and the expanded roster, both of which span the whole row.
/**
 * Move players onto a roster's injured reserve.
 *
 * Same endpoint and header shape as the Start/Sit lineup change. `reserve` is
 * declared String in Sleeper's schema but takes a list literal, exactly as
 * `starters` does on update_matchup_leg — which is the call this app already
 * makes successfully.
 *
 * The whole reserve list is sent, not a delta, so the players already on it
 * have to be included or they come straight back off.
 */
async function submitReserve({ leagueId, rosterId, reserve, token, signal }) {
  const list = reserve.map((id) => JSON.stringify(String(id))).join(",");
  const query = `mutation roster_update_reserve {
    roster_update_reserve(league_id: ${JSON.stringify(String(leagueId))},roster_id: ${Number(rosterId)},reserve: [${list}]){
      league_id roster_id players starters reserve taxi
    }
  }`;

  const res = await fetch("https://sleeper.com/graphql", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-platform": "web",
      authorization: token,
      "x-sleeper-graphql-op": "roster_update_reserve",
    },
    body: JSON.stringify({ operationName: "roster_update_reserve", variables: {}, query }),
    signal,
  });

  const json = await res.json().catch(() => null);
  // Sleeper answers 200 with an errors array rather than an HTTP error code.
  if (json?.errors?.length) {
    throw new Error(json.errors[0]?.message || "Sleeper rejected the change");
  }
  if (!res.ok) throw new Error(`Sleeper returned ${res.status}`);

  const updated = json?.data?.roster_update_reserve;
  if (!updated?.reserve) throw new Error("Sleeper returned no roster");

  // Confirm it actually took, in both directions. A silently ignored write
  // would otherwise look like success and the page would show a change that
  // never happened — and for a removal, checking only that the ones we sent
  // are present would pass while nobody had actually come off.
  const got = new Set(updated.reserve.map(String));
  const want = new Set(reserve.map(String));
  const missing = [...want].filter((id) => !got.has(id));
  const stillThere = [...got].filter((id) => !want.has(id));
  if (missing.length || stillThere.length) {
    throw new Error(
      "Sleeper accepted the request but the reserve list came back different. The roster has not changed."
    );
  }
  return updated;
}

const LEAGUE_COLUMNS = 8;

function WaiverRows({ txns, names }) {
  return txns
    .slice()
    .sort((a, b) => b.created - a.created)
    .map(t => {
      const date = new Date(t.created).toLocaleDateString();
      const adds = Object.keys(t.adds || {});
      const drops = Object.keys(t.drops || {});
      const bid = t.settings?.waiver_bid;
      const statusClass = t.status === "complete" ? "waiver-won"
        : t.status === "failed" ? "waiver-lost"
        : t.status === "proposed" ? "waiver-proposed"
        : "waiver-pending";
      const statusLabel = t.status === "complete" ? "Won"
        : t.status === "failed" ? "Lost"
        : t.status === "proposed" ? "Proposed"
        : "Pending";
      return (
        <div key={t.transaction_id} className="waiver-row">
          <span className="waiver-date">{date}</span>
          <span className={statusClass}>{statusLabel}</span>
          {adds.length > 0 && (
            <span>+ {adds.map(id => names[id] || id).join(", ")}</span>
          )}
          {drops.length > 0 && (
            <span className="waiver-drop">− {drops.map(id => names[id] || id).join(", ")}</span>
          )}
          {bid != null && <span className="waiver-bid">${bid}</span>}
        </div>
      );
    });
}

function LeagueList() {
  const { userName } = useParams();
  const [userId, setUserId] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const auth = useSleeperAuth();
  const [injuryReport, setInjuryReport] = useState({});
  const [teamStatsMap, setTeamStatsMap] = useState({});
  const [portfolioData, setPortfolioData] = useState([]); // State for portfolio data
  const [activeTab, setActiveTab] = useState("Waivers"); // State for active tab
  const [expandedLeagueIds, setExpandedLeagueIds] = useState(new Set());
  const [playerData, setPlayerData] = useState({});
  const [expandedTeams, setExpandedTeams] = useState(new Set());
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [highlightedLeagues, setHighlightedLeagues] = useState(new Set());
  // Set when a query described more than one player, so the page can say which
  // one it picked rather than appearing to light up the wrong man.
  const [searchNote, setSearchNote] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Player fuzzy search state
  const [playerFilteredList, setPlayerFilteredList] = useState([]);
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);
  const [playerSelectedIndex, setPlayerSelectedIndex] = useState(-1);
  // Owner username fuzzy search state (similar to BestballList opponent search)
  const [ownerInput, setOwnerInput] = useState("");
  const [ownerFilteredUsernames, setOwnerFilteredUsernames] = useState([]);
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [ownerSelectedIndex, setOwnerSelectedIndex] = useState(-1);
      const [usernameMap, setUsernameMap] = useState({});
  const [rosterCache, setRosterCache] = useState({}); // Cache for roster data
  const [dfsProjections, setDfsProjections] = useState({}); // DFS projections data

  const [showAllInjuries, setShowAllInjuries] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null); // Filter by position
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [dynastyOnly, setDynastyOnly] = useState(true); // Filter for dynasty leagues only

  const [waiverData, setWaiverData] = useState({});       // { league_id: Transaction[] }
  const [waiverPlayerNames, setWaiverPlayerNames] = useState({}); // { player_id: name }
  const [waiverLoading, setWaiverLoading] = useState(false);
  const [waiverArchiveOpen, setWaiverArchiveOpen] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(null);

  // Add state for sorting
  const [sortConfig, setSortConfig] = useState({
    key: "count",
    direction: "descending",
  });

  // The league table has its own sort, separate from the portfolio table above.
  // Three states rather than two: Sleeper's own order is the default and worth
  // being able to get back to, so a third click clears the sort.
  const [leagueSort, setLeagueSort] = useState(NO_LEAGUE_SORT);

  let searchTimeout;

  const handleToggle = (leagueId) => {
    setExpandedLeagueIds((prevIds) => {
      const newIds = new Set(prevIds);
      if (newIds.has(leagueId)) {
        newIds.delete(leagueId);
      } else {
        newIds.add(leagueId);
      }
      return newIds;
    });
  };

  const handleTeamToggle = (teamAbbreviation) => {
    setExpandedTeams((prevIds) => {
      const newIds = new Set(prevIds);
      if (newIds.has(teamAbbreviation)) {
        newIds.delete(teamAbbreviation);
      } else {
        newIds.add(teamAbbreviation);
      }
      return newIds;
    });
  };

  const handlePlayerClick = (player) => {
    const playerId = playerKey(player.first_name, player.last_name);

    if (selectedPlayer === playerId) {
      setSelectedPlayer(null);
      setHighlightedLeagues(new Set());
    } else {
      // Clear any owner selection/inputs when selecting a player
      setOwnerInput("");
      setSearchQuery("");
      setOwnerFilteredUsernames([]);
      setShowOwnerDropdown(false);
      setOwnerSelectedIndex(-1);

      setSearchNote(null);
      setSelectedPlayer(playerId);
      highlightLeaguesWithPlayer(player.first_name, player.last_name);
    }
  };

  const handlePlayerDoubleClick = (player) => {
    const fullName = `${player.first_name} ${player.last_name}`;
    navigator.clipboard.writeText(fullName).then(
      () => {
        console.log(`Copied ${fullName} to clipboard`);
      },
      (err) => {
        console.error("Failed to copy to clipboard: ", err);
      }
    );
  };

  const highlightLeaguesWithPlayer = (firstName, lastName) => {
    setHighlightedLeagues(
      new Set(leaguesWithPlayer(firstName, lastName, playerData))
    );
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value; // Allow spaces in the query
    setSearchQuery(query);

    // Update dropdown suggestions based on player's teams
    if (query.trim()) {
      const filtered = filterPlayers(query);
      setPlayerFilteredList(filtered);
      setShowPlayerDropdown(filtered.length > 0);
      setPlayerSelectedIndex(-1);
    } else {
      setShowPlayerDropdown(false);
      setPlayerFilteredList([]);
    }

    clearTimeout(window.searchTimeout);

    if (query.trim().length < 3) {
      setSelectedPlayer(null);
      setHighlightedLeagues(new Set());
      setSearchNote(null);
    } else {
      if (searchTimeout) {
        clearTimeout(searchTimeout); // Clear the previous timeout
      }
      window.searchTimeout = setTimeout(() => searchPlayer(query.trim()), 1000); // Delay search for 1 second
    }
  };

  // Collect all player display names from the user's teams (playerData)
  const collectPlayerNames = () => {
    const names = new Set();
    Object.values(playerData).forEach((leagueData) => {
      if (!leagueData || !leagueData.players) return;
      Object.values(leagueData.players).forEach((p) => {
        if (p && p.first_name && p.last_name) {
          names.add(`${p.first_name} ${p.last_name}`);
        }
      });
    });
    return Array.from(names).sort();
  };

  const filterPlayers = (input) => {
    if (!input.trim()) return [];
    const all = collectPlayerNames();
    const q = input.toLowerCase();
    // fuzzy-ish: include if full name includes the query or any part starts with query
    const filtered = all.filter((name) => {
      const lower = name.toLowerCase();
      if (lower.includes(q)) return true;
      const parts = q.split(" ").filter(Boolean);
      return parts.every(
        (part) =>
          parts.length && lower.split(" ").some((pn) => pn.startsWith(part))
      );
    });
    return filtered.slice(0, 12);
  };

  const handlePlayerKeydown = (e) => {
    if (!showPlayerDropdown) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setPlayerSelectedIndex((prev) =>
          prev < playerFilteredList.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setPlayerSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (
          playerSelectedIndex >= 0 &&
          playerFilteredList[playerSelectedIndex]
        ) {
          const name = playerFilteredList[playerSelectedIndex];
          handlePlayerSelect(name);
        }
        break;
      case "Escape":
        setShowPlayerDropdown(false);
        break;
      default:
        break;
    }
  };

  const handlePlayerSelect = (name) => {
    // Clear owner selection when picking a player from suggestions
    setOwnerInput("");
    setOwnerFilteredUsernames([]);
    setShowOwnerDropdown(false);
    setOwnerSelectedIndex(-1);

    setSearchQuery(name);
    setShowPlayerDropdown(false);
    setPlayerSelectedIndex(-1);
    // Run the existing search directly (immediate)
    searchPlayer(name);
  };

  // --- Username caching / fuzzy search helpers ---
  const getUsernameFromId = async (userId) => {

    const userMapKey = "sleeperUserMap";
    let userMap = JSON.parse(localStorage.getItem(userMapKey) || "{}");

    if (userMap[userId]) return userMap[userId];


    try {
      const response = await fetch(`https://api.sleeper.app/v1/user/${userId}`);
      if (response.ok) {
        const userData = await response.json();
        const username =
          userData.username || userData.display_name || `User_${userId}`;

        userMap[userId] = username;
                localStorage.setItem(userMapKey, JSON.stringify(userMap));
        return username;
      }
    } catch (error) {
      console.error(`Error fetching username for user ${userId}:`, error);
    }

    const fallback = `User_${userId}`;
    userMap[userId] = fallback;
    localStorage.setItem(userMapKey, JSON.stringify(userMap));
    return fallback;
  };

  const getAllUsernames = useCallback(async (userIds) => {
    const userMapKey = "sleeperUserMap";
    let userMap = JSON.parse(localStorage.getItem(userMapKey) || "{}");

    const uniqueUserIds = [...new Set(userIds.filter((id) => id))];

    const uncached = uniqueUserIds.filter((id) => !userMap[id]);
    
    if (uncached.length === 0) {
      setUsernameMap(userMap);
      return userMap;
    }

    const fetched = await Promise.all(
      uncached.map((id) => getUsernameFromId(id))
    );
    
    uncached.forEach((id, i) => {
      userMap[id] = fetched[i];
    });
    localStorage.setItem(userMapKey, JSON.stringify(userMap));
    setUsernameMap(userMap);
    return userMap;
  }, []);

  const loadLocalUsernames = () => {
    const userMapKey = "sleeperUserMap";
    const stored = JSON.parse(localStorage.getItem(userMapKey) || "{}");
    setUsernameMap(stored);
  };

  // Filter usernames (prefix match) for dropdown suggestions
  const filterUsernames = (input) => {
    if (!input.trim()) return [];
    const usernames = Object.values(usernameMap);
    return usernames
      .filter((username) =>
        username.toLowerCase().startsWith(input.toLowerCase())
      )
      .slice(0, 10);
  };

  // When the owner input is focused and we have few cached usernames, try to fetch owner ids from leagues' rosters
  const fetchOwnerIdsFromLeagues = useCallback(async () => {
    try {
      const ownerIds = [];
      const newRosterCache = {};
      
      for (const league of leagues) {
        try {
          const resp = await fetch(
            `https://api.sleeper.app/v1/league/${league.league_id}/rosters`
          );
          if (!resp.ok) continue;
          const rosters = await resp.json();
          
          // Cache the roster data
          newRosterCache[league.league_id] = rosters;
          
          rosters.forEach((r) => {
            if (r.owner_id) ownerIds.push(r.owner_id);
          });
        } catch (e) {
          // ignore and continue
        }
      }
      
      // Update the cache
      setRosterCache(newRosterCache);
      
      if (ownerIds.length > 0) {
        await getAllUsernames(ownerIds);
      }
    } catch (e) {
      console.error("Error fetching owner ids from leagues:", e);
    }
  }, [leagues, getAllUsernames]);

  // Handlers for owner (username) input
  const handleOwnerInputChange = (e) => {
    const v = e.target.value;
    setOwnerInput(v);
    if (v.trim()) {
      const filtered = filterUsernames(v);
      setOwnerFilteredUsernames(filtered);
      setShowOwnerDropdown(filtered.length > 0);
      setOwnerSelectedIndex(-1);
    } else {
      setShowOwnerDropdown(false);
      setOwnerFilteredUsernames([]);
    }
  };

  const handleOwnerKeydown = (e) => {
    if (!showOwnerDropdown) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setOwnerSelectedIndex((prev) =>
          prev < ownerFilteredUsernames.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setOwnerSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (
          ownerSelectedIndex >= 0 &&
          ownerFilteredUsernames[ownerSelectedIndex]
        ) {
          const username = ownerFilteredUsernames[ownerSelectedIndex];
          setOwnerInput(username);
          setShowOwnerDropdown(false);
          setOwnerSelectedIndex(-1);
          // TODO: you may want to act on selection (e.g., highlight leagues)
        }
        break;
      case "Escape":
        setShowOwnerDropdown(false);
        break;
      default:
        // Do nothing, so normal typing/backspace works
        break;
    }
  };

  const handleOwnerSelect = (username) => {
    setOwnerInput(username);
    setShowOwnerDropdown(false);
    setOwnerSelectedIndex(-1);
    // Highlight leagues where this owner appears
    (async () => {
      try {
        // Clear any player selection/inputs when selecting an owner
        setSelectedPlayer(null);
        setSearchQuery("");
        setShowPlayerDropdown(false);
        setPlayerFilteredList([]);
        setPlayerSelectedIndex(-1);

        const ownerId = await resolveUsernameToId(username);
        if (!ownerId) {
          setHighlightedLeagues(new Set());
          return;
        }

        const highlighted = new Set();
        // Use cached roster data instead of making new requests
        leagues.forEach((league) => {
          const rosters = rosterCache[league.league_id] || [];
          const hasOwner = rosters.some((r) => r.owner_id === ownerId);
          if (hasOwner) highlighted.add(league.league_id);
        });

        // ensure we set a fresh Set instance so React re-renders
        const highlightedSet = new Set(highlighted);
        console.log(
          "Owner select:",
          username,
          ownerId,
          "highlighted count:",
          highlightedSet.size
        );
        setHighlightedLeagues(highlightedSet);
      } catch (e) {
        console.error("Error highlighting owner leagues:", e);
        setHighlightedLeagues(new Set());
      }
    })();
  };

  // Resolve a username to a sleeper user_id using cached map or API
  const resolveUsernameToId = async (username) => {
    if (!username) return null;
    const lower = username.toLowerCase();
    
    // Check in current state
    for (const [id, name] of Object.entries(usernameMap || {})) {
      if (name && name.toLowerCase() === lower) {
        return id;
      }
    }
    
    // Check localStorage
    const userMapKey = "sleeperUserMap";
    const stored = JSON.parse(localStorage.getItem(userMapKey) || "{}");
    for (const [id, name] of Object.entries(stored)) {
      if (name && name.toLowerCase() === lower) {
        return id;
      }
    }

    try {
      const resp = await fetch(`https://api.sleeper.app/v1/user/${username}`);
      if (resp.ok) {
        const data = await resp.json();
        const id = data.user_id;
        const combined = {
          ...(stored || {}),
          [id]: data.username || data.display_name,
        };
        localStorage.setItem(userMapKey, JSON.stringify(combined));
        setUsernameMap(combined);
        return id;
      }
    } catch (e) {
      console.error("Error resolving username to id:", e);
    }

    return null;
  };

  useEffect(() => {
    loadLocalUsernames();
  }, []);

  // Resolves to one player and lights only that player's leagues. It used to
  // collect the leagues of every player whose name contained the query and
  // then highlight whichever match the loops reached last, so "etienne" lit
  // four leagues while naming Travis Etienne, who was in one of them.
  const searchPlayer = (query) => {
    const hit = resolvePlayerSearch(query, playerData);

    if (!hit) {
      setSelectedPlayer(null);
      setHighlightedLeagues(new Set());
      setSearchNote(null);
      return;
    }

    setSelectedPlayer(hit.key);
    setHighlightedLeagues(new Set(hit.leagueIds));
    setSearchNote(
      hit.otherMatches > 0
        ? `Showing ${hit.firstName} ${hit.lastName} — ${hit.otherMatches} other ` +
          `player${hit.otherMatches === 1 ? "" : "s"} also match. Pick from the list to change.`
        : null
    );
  };

  const fetchPlayerData = useCallback(
    async (leagues) => {
      const requests = {
        username: userName,
        league: leagues.map((league) => {
          const leagueId = league.league_id;
          const playerlist = [
            ...league.userRoster.starters,
            ...league.userRoster.reserve,
            ...league.userRoster.taxi,
            ...league.userRoster.uniquePlayers,
          ];
          return {
            league_id: leagueId,
            playerlist,
          };
        }),
      };

      try {
        const response = await fetch(`${BASE_URL}/getplayers`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requests),
        });

        const data = await response.json();
        const playerDataMap = {};
        data.forEach((leagueData) => {
          playerDataMap[leagueData.league_id] = leagueData;
        });
        setPlayerData(playerDataMap);

        const playerCountMap = {};

        // Count players across leagues, excluding playerId === 0
        leagues.forEach((league) => {
          const players = [
            ...league.userRoster.starters,
            ...league.userRoster.reserve,
            ...league.userRoster.taxi,
            ...league.userRoster.uniquePlayers,
          ];

          players.forEach((playerId) => {
            if (playerId && playerId !== "0") {
              // Ensure playerId is valid and not 0
              if (!playerCountMap[playerId]) {
                playerCountMap[playerId] = 0;
              }
              playerCountMap[playerId]++;
            }
          });
        });
        console.log("Player Count Map: ", playerCountMap);
        // Portfolio data will be fetched separately when Portfolio tab is opened
      } catch (error) {
        console.error("Error fetching player data:", error);
      }
    },
    [userName]
  );

  // Sorting function
  const handleSort = (key, defaultDirection = "descending") => {
    setSortConfig((prevConfig) => {
      const isSameKey = prevConfig.key === key;
      const newDirection = isSameKey
        ? prevConfig.direction === "ascending"
          ? "descending"
          : "ascending"
        : defaultDirection;
      return { key, direction: newDirection };
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "ascending" ? "↑" : "↓";
  };

  // ascending → descending → back to Sleeper's order.
  const cycleLeagueSort = (key) =>
    setLeagueSort((prev) => nextLeagueSort(prev, key));

  const getLeagueSortIcon = (key) => {
    if (leagueSort.key !== key) return "⇅";
    return leagueSort.direction === "ascending" ? "↑" : "↓";
  };

  const orderedLeagues = () =>
    orderLeagues(leagues, { dynastyOnly, sort: leagueSort });

  const fetchPortfolioData = useCallback(async (leagues) => {
    if (!leagues || leagues.length === 0) return;

    // Build player count map, and the scorings each player is rostered under —
    // this row spans every league, so there is no single scoring to price it in.
    const playerCountMap = {};
    const playerLeagues = {};
    leagues.forEach((league) => {
      const players = [
        ...league.userRoster.starters,
        ...league.userRoster.reserve,
        ...league.userRoster.taxi,
        ...league.userRoster.uniquePlayers,
      ];

      players.forEach((playerId) => {
        if (playerId && playerId !== "0") {
          if (!playerCountMap[playerId]) {
            playerCountMap[playerId] = 0;
            playerLeagues[playerId] = [];
          }
          playerCountMap[playerId]++;
          if (league.scoring_settings) {
            playerLeagues[playerId].push(league.scoring_settings);
          }
        }
      });
    });

    const playerIds = Object.keys(playerCountMap);

    if (playerIds.length === 0) {
      setPortfolioData([]);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/getplayers/data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          playerlist: playerIds,
        }),
      });

      const data = await response.json();

      // Sleeper's season stat lines, so the rate can be priced in each league's
      // own scoring rather than in PPR regardless of what the league plays.
      // One request, shared by every row.
      let seasonStats = {};
      try {
        const keepKeys = scoringStatKeys(leagues);
        const rows = await fetch(seasonStatsUrl(SEASON)).then((r) => {
          if (!r.ok) throw new Error(String(r.status));
          return r.json();
        });
        seasonStats = reduceStatRows(rows, keepKeys);
      } catch (err) {
        // Without it the column falls back to what it always showed.
        console.error("Error fetching season stats for portfolio:", err);
      }

      // Map player data to portfolio format
      const portfolio = Object.entries(data).map(([playerId, player]) => {
        const count = playerCountMap[playerId] || 0;
        const percentage = leagues.length > 0
          ? ((count / leagues.length) * 100).toFixed(0)
          : "0";

        // Averaged over every scoring this player is rostered under: a tight
        // end held in three TE-premium leagues should read higher than one held
        // in three PPR leagues, which is what this column is read for.
        const leagueScored = averageFptsPerGame(
          seasonStats[playerId],
          playerLeagues[playerId],
          projectedPoints
        );
        const fpts_g = leagueScored != null
          ? leagueScored.toFixed(1)
          // Sleeper has no stat line for them, so fall back to the plain PPR rate.
          : (player.pts_ppr !== null && player.pts_ppr !== undefined && player.gp && player.gp > 0)
            ? (player.pts_ppr / player.gp).toFixed(1)
            : "N/A";

        return {
          name: `${player.first_name || ""} ${player.last_name || ""}`.trim(),
          position: player.position || "N/A",
          count,
          percentage,
          fpts_g,
        };
      });

      setPortfolioData(portfolio);
    } catch (error) {
      console.error("Error fetching portfolio data:", error);
      setPortfolioData([]);
    }
  }, []);

  const fetchDfsProjections = useCallback(async (week) => {
    try {
      // Check cache first
      const cacheKey = `dfs_projections_week_${week}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);
      
      const now = Date.now();
      const cacheExpiry = 60 * 60 * 1000; // 1 hour
      
      if (cachedData && cacheTimestamp && (now - parseInt(cacheTimestamp)) < cacheExpiry) {
        const data = JSON.parse(cachedData);
        setDfsProjections(data);
        return;
      }
      
      // Fetch from API
      const response = await fetch(`${BASE_URL}/dfs-salaries/week/${week}`);
      const data = await response.json();
      
      // Cache the data
      sessionStorage.setItem(cacheKey, JSON.stringify(data));
      sessionStorage.setItem(`${cacheKey}_timestamp`, now.toString());
      
      setDfsProjections(data);
    } catch (error) {
      console.error('Error fetching DFS projections:', error);
    }
  }, []);

  const fetchLeagueData = useCallback(async () => {
    try {
      const userResponse = await fetch(
        `https://api.sleeper.app/v1/user/${userName}`
      );
      const userData = await userResponse.json();
      const userId = userData.user_id;
      setUserId(userId);

      const leaguesResponse = await fetch(
        `https://api.sleeper.app/v1/user/${userId}/leagues/nfl/${SEASON}`
      );
      const leaguesData = await leaguesResponse.json();

      const filteredLeagues = visibleLeagues(
        leaguesData.filter(
          (league) =>
            league.settings.best_ball === 0 || league.settings.best_ball == null
        ),
        // Leagues switched off in Settings.
        loadHiddenLeagues()
      );

      // Needed before the per-league fan-out below, because the lineup comes
      // from the week's matchup rather than from the roster. Cached for an
      // hour, so this is usually not a request at all.
      const lineupWeek = await getCurrentWeek();

      const leagueDetailsPromises = filteredLeagues.map(async (league) => {
        const leagueId = league.league_id;

        // In parallel: the squad from the roster, the lineup from the matchup.
        // See sleeperLineup.js for why the roster's own `starters` is not used.
        const [rostersData, matchupRows] = await Promise.all([
          fetch(`https://api.sleeper.app/v1/league/${leagueId}/rosters`).then((r) => r.json()),
          lineupWeek > 0 ? fetchMatchups(leagueId, lineupWeek) : Promise.resolve([]),
        ]);

        const userRoster = rostersData.find(
          (roster) => roster.owner_id === userId
        );

        if (!userRoster) {
          return null;
        }

        // Every roster in the league is already here — only ours was being kept.
        // Points arrive split into an integer and a hundredths part.
        const exactPoints = (settings, key) =>
          (settings?.[key] || 0) + (settings?.[`${key}_decimal`] || 0) / 100;
        const placeOf = (sorted) =>
          sorted.findIndex((r) => r.roster_id === userRoster.roster_id) + 1;

        // Sleeper's default standings order: wins, then points scored.
        const standingRank = placeOf(
          [...rostersData].sort(
            (a, b) =>
              (b.settings?.wins || 0) - (a.settings?.wins || 0) ||
              exactPoints(b.settings, "fpts") - exactPoints(a.settings, "fpts")
          )
        );
        const maxPfRank = placeOf(
          [...rostersData].sort(
            (a, b) => exactPoints(b.settings, "ppts") - exactPoints(a.settings, "ppts")
          )
        );

        const starters = startersFor(matchupRows, userRoster);
        const reserve = userRoster.reserve || [];
        const taxi = userRoster.taxi || [];
        const players = userRoster.players || [];

        const uniquePlayers = players.filter(
          (player) =>
            !starters.includes(player) &&
            !reserve.includes(player) &&
            !taxi.includes(player)
        );

        return {
          ...league,
          standingRank,
          maxPfRank,
          rankTotal: rostersData.length,
          emptyBenchSlots: emptyBenchSlots({
            rosterPositions: league.roster_positions,
            players,
            reserve,
            taxi,
          }),
          userRoster: {
            starters,
            uniquePlayers,
            reserve,
            taxi,
            // The reserve mutation is addressed by roster_id, not owner_id.
            rosterId: userRoster.roster_id,
            settings: userRoster.settings,
          },
        };
      });

      const leagueDetails = await Promise.all(leagueDetailsPromises);
      const filteredLeagueDetails = leagueDetails.filter(
        (league) => league !== null
      );

      setLeagues(filteredLeagueDetails);
      await fetchPlayerData(filteredLeagueDetails);
    } catch (error) {
      console.error("Error fetching league data:", error);
    }
  }, [userName, fetchPlayerData]);

  // Only the signed-in manager's own leagues can be changed, and only with a
  // real Sleeper login — the free-text username field is not enough to write.
  const canEditRosters =
    !!auth?.token &&
    !!auth?.display_name &&
    auth.display_name.toLowerCase() === (userName || "").toLowerCase();

  const irIssues = useMemo(
    () =>
      findIrIssues(
        leagues,
        (leagueId, playerId) => playerData[leagueId]?.players?.[playerId]
      ),
    [leagues, playerData]
  );

  const applyIr = useCallback(
    async (picks) => {
      const results = [];
      const applied = [];

      // One league at a time. Sleeper is asked for no more than it needs, and a
      // league that refuses does not stop the rest from going through.
      for (const { issue, playerIds } of picks) {
        if (!playerIds.length) continue;
        try {
          // The whole list is sent, not a delta, so whoever is already on
          // reserve has to go along with it.
          const reserve = [...issue.reserve, ...playerIds];
          await submitReserve({
            leagueId: issue.leagueId,
            rosterId: issue.rosterId,
            reserve,
            token: auth?.token,
          });
          applied.push({ leagueId: issue.leagueId, reserve, playerIds });
          results.push({ leagueId: issue.leagueId, leagueName: issue.leagueName });
        } catch (err) {
          results.push({
            leagueId: issue.leagueId,
            leagueName: issue.leagueName,
            error: err?.message || String(err),
          });
        }
      }

      // Reflect what landed without refetching every league. A player moving to
      // reserve leaves the bench, which is also what frees the roster spot the
      // Empty bench column counts.
      if (applied.length) {
        setLeagues((prev) =>
          prev.map((l) => {
            const change = applied.find((a) => a.leagueId === l.league_id);
            if (!change) return l;
            const moved = new Set(change.playerIds.map(String));
            const userRoster = {
              ...l.userRoster,
              reserve: change.reserve,
              uniquePlayers: (l.userRoster.uniquePlayers || []).filter(
                (id) => !moved.has(String(id))
              ),
            };
            return {
              ...l,
              userRoster,
              emptyBenchSlots: emptyBenchSlots({
                rosterPositions: l.roster_positions,
                players: [
                  ...userRoster.starters,
                  ...userRoster.uniquePlayers,
                  ...userRoster.reserve,
                  ...userRoster.taxi,
                ],
                reserve: userRoster.reserve,
                taxi: userRoster.taxi,
              }),
            };
          })
        );
      }

      return results;
    },
    [auth]
  );

  const irReturnIssues = useMemo(
    () =>
      findIrReturnIssues(
        leagues,
        (leagueId, playerId) => playerData[leagueId]?.players?.[playerId]
      ),
    [leagues, playerData]
  );

  const applyReturn = useCallback(
    async (picks) => {
      const results = [];
      const applied = [];

      for (const { issue, playerIds } of picks) {
        if (!playerIds.length) continue;
        try {
          const moved = new Set(playerIds.map(String));
          // The whole list is sent, not a delta — here that means the ones
          // staying put, with the selected players taken out of it.
          const reserve = issue.reserve.filter((id) => !moved.has(String(id)));
          await submitReserve({
            leagueId: issue.leagueId,
            rosterId: issue.rosterId,
            reserve,
            token: auth?.token,
          });
          applied.push({ leagueId: issue.leagueId, reserve, playerIds });
          results.push({ leagueId: issue.leagueId, leagueName: issue.leagueName });
        } catch (err) {
          results.push({
            leagueId: issue.leagueId,
            leagueName: issue.leagueName,
            error: err?.message || String(err),
          });
        }
      }

      // Coming off reserve means going back onto the bench, which is the
      // mirror of applyIr: the roster spot they now take up is one the Empty
      // bench column was counting as free.
      if (applied.length) {
        setLeagues((prev) =>
          prev.map((l) => {
            const change = applied.find((a) => a.leagueId === l.league_id);
            if (!change) return l;
            const userRoster = {
              ...l.userRoster,
              reserve: change.reserve,
              uniquePlayers: [...(l.userRoster.uniquePlayers || []), ...change.playerIds],
            };
            return {
              ...l,
              userRoster,
              emptyBenchSlots: emptyBenchSlots({
                rosterPositions: l.roster_positions,
                players: [
                  ...userRoster.starters,
                  ...userRoster.uniquePlayers,
                  ...userRoster.reserve,
                  ...userRoster.taxi,
                ],
                reserve: userRoster.reserve,
                taxi: userRoster.taxi,
              }),
            };
          })
        );
      }

      return results;
    },
    [auth]
  );

  const getOrdinalSuffix = (num) => {
    if (!num) return '';
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  const fetchInjuryReport = useCallback(async () => {
    try {
      const [injuryRes, statsRes] = await Promise.all([
        fetch(`${BASE_URL}/teams`, { method: "GET", headers: { "Content-Type": "application/json" } }),
        fetch(`${BASE_URL}/stats/teams`, { method: "GET", headers: { "Content-Type": "application/json" } }),
      ]);

      const injuryData = await injuryRes.json();
      setInjuryReport(injuryData);

      const statsData = await statsRes.json();
      if (Array.isArray(statsData)) {
        const map = {};
        for (const t of statsData) map[t.team] = t;
        setTeamStatsMap(map);
      }
    } catch (error) {
      console.error("Error fetching injury report:", error);
    }
  }, []);

  const waiverLoadingRef = React.useRef(false);
  const fetchWaiverData = useCallback(async () => {
    if (!userId || currentWeek === null || leagues.length === 0) return;
    if (waiverLoadingRef.current) return;
    waiverLoadingRef.current = true;

    // Check 30-min cache
    const cacheKey = `waiver_cache_${userName}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || "null");
      if (cached && Date.now() - cached.ts < 30 * 60 * 1000) {
        setWaiverData(cached.waiverData);
        setWaiverPlayerNames(cached.playerNames);
        return;
      }
    } catch {}

    setWaiverLoading(true);
    waiverLoadingRef.current = true;

    // Check if the current userName matches a linked Sleeper account
    let sleeperToken = null;
    try {
      const auth = JSON.parse(localStorage.getItem("sleeper_auth") || "null");
      if (auth && auth.display_name?.toLowerCase() === userName?.toLowerCase()) {
        sleeperToken = auth.token;
      }
    } catch {}

    try {
      // Preseason: state reports week 0 but Sleeper stores transactions under week 1
      const weeks = currentWeek === 0 ? [1] : [currentWeek];
      const allPlayerIds = new Set();
      const newWaiverData = {};

      await Promise.all(
        leagues.map(async (league) => {
          const txnArrays = await Promise.all(
            weeks.map(w =>
              fetch(`https://api.sleeper.app/v1/league/${league.league_id}/transactions/${w}`)
                .then(r => r.json())
                .catch(() => [])
            )
          );
          const merged = txnArrays.flat();
          const seen = new Set();
          const deduped = merged.filter(t => {
            if (seen.has(t.transaction_id)) return false;
            seen.add(t.transaction_id);
            return true;
          });
          const mine = deduped.filter(
            t => (t.type === "waiver" || t.type === "free_agent") && t.creator === userId
          );

          // If token is available, fetch all transactions via authenticated GraphQL
          // This returns pending + complete + failed in one call with player_map included
          let graphqlTxns = [];
          const graphqlPlayerMap = {};
          let graphqlSucceeded = false;
          if (sleeperToken) {
            try {
              const rosterId = league.userRoster?.roster_id;
              const res = await fetch("https://sleeper.com/graphql", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "x-platform": "web",
                  "authorization": sleeperToken,
                  "x-sleeper-graphql-op": "league_transactions_filtered",
                },
                body: JSON.stringify({
                  operationName: "league_transactions_filtered",
                  query: `query league_transactions_filtered { league_transactions_filtered(league_id: "${league.league_id}", roster_id_filters: ${rosterId != null ? `[${rosterId}]` : "[]"}, type_filters: ["waiver"], leg_filters: [], status_filters: []) { transaction_id type status adds drops leg settings waiver_budget creator created player_map } }`,
                  variables: {},
                }),
              }).then(r => r.json());
              if (res?.data?.league_transactions_filtered) {
                graphqlTxns = res.data.league_transactions_filtered.filter(t => t.creator === userId);
                graphqlTxns.forEach(t => {
                  Object.entries(t.player_map || {}).forEach(([pid, info]) => {
                    graphqlPlayerMap[pid] = `${info.first_name || ""} ${info.last_name || ""}`.trim() || pid;
                  });
                });
                graphqlSucceeded = true;
              }
            } catch (e) {
              console.error("GraphQL waiver fetch failed:", e);
            }
          }

          // Use GraphQL results if request succeeded, else fall back to REST
          const all = graphqlSucceeded ? graphqlTxns : mine;
          newWaiverData[league.league_id] = all;
          // For non-GraphQL transactions still collect player IDs for REST name resolution
          all.forEach(t => {
            if (!graphqlPlayerMap[Object.keys(t.adds || {})[0]]) {
              Object.keys(t.adds || {}).forEach(id => allPlayerIds.add(id));
              Object.keys(t.drops || {}).forEach(id => allPlayerIds.add(id));
            }
          });
          // Merge inline player names into shared map
          Object.assign(graphqlPlayerMap, {}); // will be merged after loop
          league._graphqlPlayerMap = graphqlPlayerMap;
        })
      );

      setWaiverData(newWaiverData);

      // Collect inline player names from GraphQL player_map
      const inlineNames = {};
      leagues.forEach(league => {
        if (league._graphqlPlayerMap) {
          Object.assign(inlineNames, league._graphqlPlayerMap);
          delete league._graphqlPlayerMap;
        }
      });

      // Resolve remaining player IDs via REST for non-GraphQL transactions
      const nameMap = { ...inlineNames };
      if (allPlayerIds.size > 0) {
        const response = await fetch(`${BASE_URL}/getplayers/data`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ playerlist: [...allPlayerIds] }),
        });
        const data = await response.json();
        Object.entries(data).forEach(([pid, info]) => {
          nameMap[pid] = info.name ||
            `${info.first_name || ""} ${info.last_name || ""}`.trim() ||
            pid;
        });
      }
      setWaiverPlayerNames(nameMap);

      // Save to 30-min cache
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), waiverData: newWaiverData, playerNames: nameMap }));
      } catch {}
    } catch (err) {
      console.error("Error fetching waiver data:", err);
    } finally {
      setWaiverLoading(false);
      waiverLoadingRef.current = false;
    }
  }, [userId, currentWeek, leagues, userName]);

  useEffect(() => {
    // Fetch current week first
    getCurrentWeek().then(week => {
      if (week == null) return;
      setCurrentWeek(week);
      fetchDfsProjections(week);
    });
    
    fetchLeagueData();
    fetchInjuryReport();
  }, [fetchLeagueData, fetchInjuryReport, fetchDfsProjections]);

  useEffect(() => {
    if (!leagues || leagues.length === 0) return;
    
    // Always try to fetch usernames for the current leagues
    // This ensures we get usernames for new leagues even if we have many cached
    fetchOwnerIdsFromLeagues();
  }, [leagues, fetchOwnerIdsFromLeagues]);

  useEffect(() => {
    // Fetch portfolio data only when Portfolio tab is opened
    if (activeTab === "Portfolio" && leagues.length > 0) {
      fetchPortfolioData(leagues);
    }
  }, [activeTab, leagues, fetchPortfolioData]);

  useEffect(() => {
    if (leagues.length > 0 && currentWeek !== null) {
      fetchWaiverData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagues, currentWeek]);

  const renderPlayerInfo = (playerId, leagueId) => {
    const player = playerData[leagueId]?.players[playerId];

    if (!player) return null;

    const { first_name, last_name } = player;

    return (
      <div>
        {first_name} {last_name}
      </div>
    );
  };

  const renderInjuryStatus = (playerId, leagueId) => {
    const player = playerData[leagueId]?.players[playerId];
    if (!player) return null;

    const { injury_status } = player;
    let injuryClass = "";

    if (injury_status) {
      injuryClass =
        injury_status === "Questionable" ? "injury-questionable" : "injury";
    }

    return <div className={injuryClass}>{injury_status}</div>;
  };

  const renderPlayerLinks = (player, leagueId) => {
    const yahooId = playerData[leagueId]?.players[player]?.yahoo_id;
    const rotowireId = playerData[leagueId]?.players[player]?.rotowire_id;
    const firstName = playerData[leagueId]?.players[player]?.first_name;
    const lastName = playerData[leagueId]?.players[player]?.last_name;

    return (
      <>
        <span
          style={{
            display: "inline-block",
            marginRight: "5px",
            filter: yahooId ? "none" : "grayscale(100%)",
          }}
        >
          <a
            href={
              yahooId ? `https://sports.yahoo.com/nfl/players/${yahooId}` : "#"
            }
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: yahooId ? "inline" : "none",
              pointerEvents: yahooId ? "auto" : "none",
            }}
          >
            <img
              src="/yahoo.png"
              alt="Yahoo"
              style={{ width: "20px", height: "20px" }}
            />
          </a>
          {!yahooId && (
            <img
              src="/yahoo.png"
              alt="Yahoo"
              style={{
                width: "20px",
                height: "20px",
              }}
            />
          )}
        </span>
        <span
          style={{
            display: "inline-block",
            filter: rotowireId ? "none" : "grayscale(100%)",
            pointerEvents: rotowireId ? "auto" : "none",
          }}
        >
          <a
            href={
              rotowireId
                ? `https://www.rotowire.com/football/player/${firstName}-${lastName}-${rotowireId}`
                : "#"
            }
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: rotowireId ? "inline" : "none" }}
          >
            <img
              src="/rotowire.png"
              alt="Rotowire"
              style={{ width: "20px", height: "20px", marginRight: "5px" }}
            />
          </a>
          {!rotowireId && (
            <img
              src="/rotowire.png"
              alt="Rotowire"
              style={{
                width: "20px",
                height: "20px",
              }}
            />
          )}
        </span>
      </>
    );
  };

  // Copied before sorting. The argument is league.userRoster[group], which is
  // held in React state, and .sort() works in place — so this used to reorder
  // state during a render.
  //
  // It mattered most for `starters`, which is positional: index i is the i-th
  // non-bench slot in roster_positions. Sorting it looks harmless because a
  // lineup is usually already roughly QB, RB, WR, TE — but a flex holding a
  // running back sorts up beside the other running backs and the slot mapping
  // is gone. Nothing on this page reads that order today; Start/Sit's lineup
  // submission does.
  const sortPlayersByPosition = (playerIds, leagueId) => {
    const positionOrder = { QB: 1, RB: 2, WR: 3, TE: 4, DEF: 5 };
    return [...playerIds].sort((a, b) => {
      const posA = playerData[leagueId]?.players[a]?.position || "ZZZ";
      const posB = playerData[leagueId]?.players[b]?.position || "ZZZ";
      return (positionOrder[posA] || 99) - (positionOrder[posB] || 99);
    });
  };

  const countInjuriesForReport = (teamInjuries) => {
    let redCount = 0;
    let orangeCount = 0;

    teamInjuries.forEach((player) => {
      const injuryStatus = player?.injury_status;
      if (injuryStatus) {
        if (injuryStatus === "Questionable") {
          orangeCount += 1;
        } else {
          redCount += 1;
        }
      }
    });

    return { redCount, orangeCount };
  };

  const countInjuries = (starters, leagueId) => {
    let redCount = 0;
    let orangeCount = 0;

    starters.forEach((playerId) => {
      const injuryStatus =
        playerData[leagueId]?.players[playerId]?.injury_status;
      if (injuryStatus) {
        if (injuryStatus === "Questionable") {
          orangeCount += 1;
        } else {
          redCount += 1;
        }
      }
    });

    return { redCount, orangeCount };
  };

  const teamFullName = (abbreviation) => {
    const teams = {
      ARI: "Arizona Cardinals",
      ATL: "Atlanta Falcons",
      BAL: "Baltimore Ravens",
      BUF: "Buffalo Bills",
      CAR: "Carolina Panthers",
      CHI: "Chicago Bears",
      CIN: "Cincinnati Bengals",
      CLE: "Cleveland Browns",
      DAL: "Dallas Cowboys",
      DEN: "Denver Broncos",
      DET: "Detroit Lions",
      GB: "Green Bay Packers",
      HOU: "Houston Texans",
      IND: "Indianapolis Colts",
      JAX: "Jacksonville Jaguars",
      KC: "Kansas City Chiefs",
      LAC: "Los Angeles Chargers",
      LAR: "Los Angeles Rams",
      LV: "Las Vegas Raiders",
      MIA: "Miami Dolphins",
      MIN: "Minnesota Vikings",
      NE: "New England Patriots",
      NO: "New Orleans Saints",
      NYG: "New York Giants",
      NYJ: "New York Jets",
      PHI: "Philadelphia Eagles",
      PIT: "Pittsburgh Steelers",
      SEA: "Seattle Seahawks",
      SF: "San Francisco 49ers",
      TB: "Tampa Bay Buccaneers",
      TEN: "Tennessee Titans",
      WAS: "Washington Commanders",
    };
    return teams[abbreviation] || abbreviation;
  };

  const handleOpenModal = (league) => {
    setSelectedLeague(league);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLeague(null);
  };

  return (
    <div className="dashboard-container">
      <div className="header-container">
        <div className="draftname">
          <h1>Leagues Overview</h1>
          <span>{userName}</span>
        </div>
      </div>

      <div className="search-container league-search">
        <label className="search-label">Find a player</label>
        <div className="player-search">
          <input
            type="text"
            className="injury-report-search"
            placeholder="Player name"
            value={searchQuery}
            onChange={handleSearchInputChange}
            onKeyDown={handlePlayerKeydown}
            onFocus={() => {
              if (searchQuery.trim()) {
                const filtered = filterPlayers(searchQuery);
                setPlayerFilteredList(filtered);
                setShowPlayerDropdown(filtered.length > 0);
                setPlayerSelectedIndex(-1);
              }
            }}
          />
          {showPlayerDropdown && (
            <div className="opponent-dropdown">
              {playerFilteredList.map((name, i) => (
                <div
                  key={name}
                  className={`opponent-option ${
                    i === playerSelectedIndex ? "selected" : ""
                  }`}
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => handlePlayerSelect(name)}
                >
                  {name}
                </div>
              ))}
            </div>
          )}
        </div>
        {searchNote && <p className="league-search-note">{searchNote}</p>}
        <label className="search-label">Find an owner</label>
        <div className="owner-search">
          <input
            type="text"
            placeholder="Search owner username"
            value={ownerInput}
            onChange={handleOwnerInputChange}
            onKeyDown={handleOwnerKeydown}
            onFocus={() => {
              loadLocalUsernames();
              if (Object.keys(usernameMap).length < 5) {
                fetchOwnerIdsFromLeagues();
              }
            }}
          />
          {showOwnerDropdown && (
            <div className="opponent-dropdown">
              {ownerFilteredUsernames.map((u, i) => (
                <div
                  key={u}
                  className={`opponent-option ${
                    i === ownerSelectedIndex ? "selected" : ""
                  }`}
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => handleOwnerSelect(u)}
                >
                  {u}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="dynasty-filter-container">
          <label className="dynasty-filter-label">
            <input
              type="checkbox"
              checked={dynastyOnly}
              onChange={(e) => setDynastyOnly(e.target.checked)}
            />
            <span>Dynasty only</span>
          </label>
        </div>
      </div>

      <div className="league-main-content">
        {/* Left Side: Leagues */}
        <LeagueIssues
          issues={irIssues}
          returnIssues={irReturnIssues}
          canEdit={canEditRosters}
          onApplyIr={applyIr}
          onApplyReturn={applyReturn}
        />

        <div className="league-list-container">
          <section className="ss-card">
           <div className="ss-table-wrap">
            <table className="ss-table league-table">
             <thead>
              <tr>
            <th>League Name</th>
            <th>Record</th>
            <th
              className="league-sortable ss-center"
              onClick={() => cycleLeagueSort("standingRank")}
              title="Position in the league standings. Click to sort: up, down, then back to Sleeper's order."
            >
              Pos {getLeagueSortIcon("standingRank")}
            </th>
            <th
              className="league-sortable ss-center"
              onClick={() => cycleLeagueSort("maxPfRank")}
              title="Position by maximum points — what the roster could have scored with perfect lineups. Click to sort: up, down, then back to Sleeper's order."
            >
              MPF {getLeagueSortIcon("maxPfRank")}
            </th>
            <th>Waivers</th>
            <th
              className="ss-center ss-th-stack"
              title="Active roster spots with nobody in them. Players on IR or the taxi squad do not take one up."
            >
              Empty
              <br />
              bench
            </th>
            <th className="ss-th-stack" title="Injured players in your starting lineup">
              Injuries
              <br />
              on starters
            </th>
            <th>Actions</th>
              </tr>
             </thead>
             <tbody>

            {leagues.length > 0 ? (
              orderedLeagues().map((league, index, visibleLeagues) => {
                const groupHeading =
                  !dynastyOnly &&
                  (index === 0 ||
                    isDynastyLeague(visibleLeagues[index - 1]) !== isDynastyLeague(league))
                    ? isDynastyLeague(league)
                      ? "Dynasty"
                      : "Redraft"
                    : null;
                const { redCount, orangeCount } = countInjuries(
                  league.userRoster?.starters || [],
                  league.league_id
                );
                return (
                  <React.Fragment key={index}>
                    {groupHeading && (
                      <tr className="ss-group-row">
                        <th colSpan={LEAGUE_COLUMNS}>{groupHeading}</th>
                      </tr>
                    )}
                    <tr className="ss-row league-row">
                    <td
                      className={`league-name ${
                        highlightedLeagues.has(league.league_id)
                          ? "league-highlighted-league"
                          : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="league-expand"
                        onClick={() => handleToggle(league.league_id)}
                        aria-expanded={expandedLeagueIds.has(league.league_id)}
                        title={
                          expandedLeagueIds.has(league.league_id)
                            ? "Hide this roster"
                            : "Show this roster"
                        }
                      >
                        <FontAwesomeIcon
                          className="league-expand-icon"
                          icon={
                            expandedLeagueIds.has(league.league_id)
                              ? faChevronDown
                              : faChevronRight
                          }
                        />
                        <span className="league-expand-name">{league.name}</span>
                      </button>
                    </td>

                    <td>
                      {league.userRoster?.settings?.wins}-
                      {league.userRoster?.settings?.losses}-
                      {league.userRoster?.settings?.ties}
                    </td>
                    {[league.standingRank, league.maxPfRank].map((place, i) => {
                      // First place is the good end here, unlike the defence
                      // ranks this gradient was written for.
                      const colour = rankColor(place, league.rankTotal);
                      return (
                        <td
                          key={i}
                          className="ss-center"
                          style={colour ? { color: colour, fontWeight: 600 } : undefined}
                          title={place ? `${place} of ${league.rankTotal}` : undefined}
                        >
                          {place || "—"}
                        </td>
                      );
                    })}
                    <td>
                      {waiverData[league.league_id] ? (() => {
                        const auth = (() => { try { return JSON.parse(localStorage.getItem("sleeper_auth") || "null"); } catch { return null; } })();
                        const isLinked = auth && auth.display_name?.toLowerCase() === userName?.toLowerCase();
                        const txns = waiverData[league.league_id];
                        const p = txns.filter(t => t.status === "pending").length;
                        const w = txns.filter(t => t.status === "complete").length;
                        const l = txns.filter(t => t.status === "failed").length;
                        // Zero counts keep their colour but read as inactive
                        const zc = (n) => (n === 0 ? " waiver-zero" : "");
                        return <span className="waiver-summary">
                          {isLinked && <span className="waiver-summary-item">P:<span className={`waiver-pending${zc(p)}`}>{p}</span></span>}
                          <span className="waiver-summary-item">W:<span className={`waiver-won${zc(w)}`}>{w}</span></span>
                          <span className="waiver-summary-item">L:<span className={`waiver-lost${zc(l)}`}>{l}</span></span>
                        </span>;
                      })() : "—"}
                    </td>
                    <td className="ss-center">
                      {league.emptyBenchSlots == null ? (
                        <span className="ss-muted">—</span>
                      ) : league.emptyBenchSlots === 0 ? (
                        <span className="ss-muted">0</span>
                      ) : (
                        league.emptyBenchSlots
                      )}
                    </td>
                    <td>
                      {redCount > 0 && (
                        <>
                          <img
                            src="/cross.png"
                            alt="Injured"
                            className="medical-cross-icon"
                          />{" "}
                          {redCount}{" "}
                        </>
                      )}
                      {orangeCount > 0 && (
                        <>
                          <FontAwesomeIcon
                            icon={faQuestion}
                            style={{ color: "orange" }}
                          />{" "}
                          {orangeCount}
                        </>
                      )}
                    </td>
                    <td className="league-actions">
                      {/* Link to Sleeper league */}
                      <a
                        href={`https://sleeper.app/leagues/${league.league_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="league-link-icon"
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>

                      {/* Modal action */}
                      <FontAwesomeIcon
                        icon={faTableCells}
                        className="league-action-icon"
                        onClick={() => handleOpenModal(league)}
                      />
                    </td>
                    </tr>
                    {expandedLeagueIds.has(league.league_id) && (
                      <tr className="league-details-row">
                       <td colSpan={LEAGUE_COLUMNS}>
                      <div className="league-details">
                        {["starters", "uniquePlayers", "reserve", "taxi"].map(
                          (group, idx) =>
                            (league.userRoster[group] || []).length > 0 && (
                              <div key={idx} className="roster-group">
                                <div className="roster-grid">
                                  <div className="roster-grid-item roster-header">
                                    {group === "uniquePlayers"
                                      ? "Bench"
                                      : group.charAt(0).toUpperCase() +
                                        group.slice(1)}
                                  </div>
                                  <div className="roster-grid-item roster-header align_center">
                                    Position
                                  </div>
                                  <div className="roster-grid-item roster-header align_center">
                                    FPTS/G
                                  </div>
                                  <div className="roster-grid-item roster-header align_center">
                                    Proj Pts
                                  </div>
                                  <div className="roster-grid-item roster-header align_center" title="Defense vs Position">
                                    DvP
                                  </div>
                                  <div className="roster-grid-item roster-header align_center">
                                    Status
                                  </div>
                                  <div className="roster-grid-item roster-header align_center">
                                    KTC
                                  </div>
                                  <div className="roster-grid-item roster-header align_center">
                                    FantasyCalc
                                  </div>
                                  <div className="roster-grid-item roster-header align_center">
                                    Links
                                  </div>
                                </div>
                                {sortPlayersByPosition(
                                  league.userRoster[group],
                                  league.league_id
                                )?.map((player, index) => {
                                  const playerInfo =
                                    playerData[league.league_id]?.players[
                                      player
                                    ];
                                  const playerId = `${playerInfo?.first_name}-${playerInfo?.last_name}`;

                                  const ktcValue =
                                    playerInfo?.["KTC Value"] || "N/A"; // Access "KTC Value"
                                  const ktcDelta =
                                    playerInfo?.["KTC Delta"] || 0; // Access "KTC Delta"
                                  const fcDelta = playerInfo?.["FC Delta"] || 0; // Access "FC Delta"
                                  
                                  // Get DFS projection data for this player
                                  const dfsKey = Object.keys(dfsProjections).find(key => 
                                    dfsProjections[key]?.sleeper_id === player.toString()
                                  );
                                  const dfsData = dfsKey ? dfsProjections[dfsKey] : null;

                                  return (
                                    <div key={index} className="roster-grid">
                                      <div
                                        className={`roster-grid-item ${
                                          selectedPlayer === playerId
                                            ? "selected-player"
                                            : ""
                                        }`}
                                      >
                                        {renderPlayerInfo(
                                          player,
                                          league.league_id
                                        )}
                                      </div>
                                      <div className="roster-grid-item align_center">
                                        <PositionPill position={playerInfo?.position} />
                                      </div>
                                      <div className="roster-grid-item align_center">
                                        {playerInfo?.pts_ppr && playerInfo?.gp && playerInfo.gp > 0
                                          ? (playerInfo.pts_ppr / playerInfo.gp).toFixed(1)
                                          : 'N/A'}
                                      </div>
                                      <div className="roster-grid-item align_center">
                                        {dfsData?.projected_points?.toFixed(1) || 'N/A'}
                                      </div>
                                      {(() => {
                                        const pos = (playerInfo?.position || '').toLowerCase();
                                        const TEAM_ABBR_MAP = { LAR: 'LA', WSH: 'WAS' };
                                        const teamKey = TEAM_ABBR_MAP[playerInfo?.team] || playerInfo?.team;
                                        const opponent = teamStatsMap[teamKey]?.schedule?.opponent;
                                        const oppKey = TEAM_ABBR_MAP[opponent] || opponent;
                                        const teamStats = teamStatsMap[oppKey];
                                        const seasonRank = teamStats?.def_rank_vs_position?.season?.[pos];
                                        const r5Rank = teamStats?.def_rank_vs_position?.rolling5?.[pos];
                                        const dvpColor = getDvpColor(seasonRank);
                                        return (
                                          <div
                                            className={`roster-grid-item align_center${r5Rank ? ' dvp-has-tooltip' : ''}`}
                                            style={dvpColor ? { color: dvpColor, fontWeight: 600 } : {}}
                                            data-dvp-tooltip={r5Rank ? `L5: ${r5Rank}${getOrdinalSuffix(r5Rank)}` : undefined}
                                          >
                                            {seasonRank ? (
                                              <>
                                                {seasonRank}
                                                <sup className="ordinal-suffix">{getOrdinalSuffix(seasonRank)}</sup>
                                              </>
                                            ) : 'N/A'}
                                          </div>
                                        );
                                      })()}
                                      <div className="roster-grid-item align_center">
                                        {renderInjuryStatus(
                                          player,
                                          league.league_id
                                        )}
                                      </div>
                                      <div className="roster-grid-item align_center">
                                        <span>{ktcValue}</span>
                                        {" ("}
                                        <span
                                          className={`${
                                            ktcDelta > 0
                                              ? "value-positive"
                                              : ktcDelta < 0
                                              ? "value-negative"
                                              : "value-neutral"
                                          }`}
                                        >
                                          {ktcDelta > 0
                                            ? `+${ktcDelta}`
                                            : ktcDelta}
                                        </span>
                                        <span>)</span>
                                      </div>
                                      <div className="roster-grid-item align_center">
                                        <span>
                                          {playerInfo?.["FC Value"] || "N/A"}
                                        </span>
                                        {" ("}
                                        <span
                                          className={`${
                                            fcDelta > 0
                                              ? "value-positive"
                                              : fcDelta < 0
                                              ? "value-negative"
                                              : "value-neutral"
                                          }`}
                                        >
                                          {fcDelta > 0
                                            ? `+${fcDelta}`
                                            : fcDelta}
                                        </span>
                                        <span>)</span>
                                      </div>
                                      <div className="roster-grid-item align_center">
                                        {renderPlayerLinks(
                                          player,
                                          league.league_id
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )
                        )}
                      </div>
                       </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={LEAGUE_COLUMNS} className="ss-muted">
                  No leagues found.
                </td>
              </tr>
            )}
             </tbody>
            </table>
           </div>
          </section>
        </div>

        {/* Modal */}
        {isModalOpen && (
          <DraftModal
            league={selectedLeague}
            draftId={selectedLeague?.draft_id}
            onClose={handleCloseModal}
            userId={userId}
          />
        )}

        {/* Right Side: Tabs for Injuries and Portfolio */}
        <div className="league-right-side-container">
          {/* Tab Navigation */}
          <div className="league-tab-container">
            <button
              className={`league-tab-button ${activeTab === "Waivers" ? "active" : ""}`}
              onClick={() => setActiveTab("Waivers")}
            >
              Waivers
            </button>
            <button
              className={`league-tab-button ${
                activeTab === "Injuries" ? "active" : ""
              }`}
              onClick={() => setActiveTab("Injuries")}
            >
              Injuries
            </button>
            <button
              className={`league-tab-button ${
                activeTab === "Portfolio" ? "active" : ""
              }`}
              onClick={() => setActiveTab("Portfolio")}
            >
              Portfolio
            </button>
          </div>

          {/* Tab Content */}
          <div className="league-tab-content">
            {activeTab === "Injuries" && (
              <div className="league-injury-report-container">
                <h2>Injury Report</h2>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={showAllInjuries}
                    onChange={() => setShowAllInjuries((prev) => !prev)}
                  />
                  <span className="slider"></span>
                </label>
                <label>Show by team</label>
                {!showAllInjuries ? (
                  <div className="all-injuries-list">
                    <div className="injury-grid-header">
                      <div className="injury-grid-column">Player Name</div>
                      <div className="injury-grid-column">Team</div>
                      <div className="injury-grid-column">Status</div>
                    </div>
                    {Object.keys(injuryReport).flatMap((teamAbbreviation) =>
                      injuryReport[teamAbbreviation].map((player, index) => (
                        <div
                          key={`${teamAbbreviation}-${index}`}
                          className={`injury-grid-row ${
                            selectedPlayer ===
                            `${player.first_name}-${player.last_name}`
                              ? "selected-player"
                              : ""
                          }`}
                          onClick={() => handlePlayerClick(player)}
                          onDoubleClick={() => handlePlayerDoubleClick(player)}
                        >
                          <div className="injury-grid-column">
                            {player.first_name} {player.last_name}
                          </div>
                          <div className="injury-grid-column">
                            {teamAbbreviation}
                          </div>
                          <div className="injury-grid-column">
                            <span
                              className={`injury-status ${player.injury_status}`}
                            >
                              {player.injury_status}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="injury-report-teams">
                    {Object.keys(injuryReport).map((teamAbbreviation) => {
                      const teamInjuries = injuryReport[teamAbbreviation];
                      const { redCount, orangeCount } =
                        countInjuriesForReport(teamInjuries);

                      return (
                        <div key={teamAbbreviation} className="injury-team">
                          <span
                            className="team-name"
                            onClick={() => handleTeamToggle(teamAbbreviation)}
                          >
                            <FontAwesomeIcon
                              className="league-expander"
                              icon={
                                expandedTeams.has(teamAbbreviation)
                                  ? faChevronDown
                                  : faChevronRight
                              }
                            />{" "}
                            {teamFullName(teamAbbreviation)}
                          </span>
                          <div className="team-injury-icons">
                            {redCount > 0 && (
                              <span className="injury-icon">
                                <img
                                  src="/cross.png"
                                  alt="Injured"
                                  className="medical-cross-icon"
                                />{" "}
                                {redCount}
                              </span>
                            )}
                            {orangeCount > 0 && (
                              <span className="injury-icon">
                                <FontAwesomeIcon
                                  icon={faQuestion}
                                  style={{ color: "orange" }}
                                />{" "}
                                {orangeCount}
                              </span>
                            )}
                          </div>
                          {expandedTeams.has(teamAbbreviation) && (
                            <div className="team-injury-list">
                              {teamInjuries.map((player, index) => (
                                <div
                                  key={index}
                                  className={`injury-player ${
                                    selectedPlayer ===
                                    `${player.first_name}-${player.last_name}`
                                      ? "selected-player"
                                      : ""
                                  }`}
                                  onClick={() => handlePlayerClick(player)}
                                >
                                  {player.first_name} {player.last_name}
                                  <span
                                    className={`injury-status ${player.injury_status}`}
                                  >
                                    {player.injury_status}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === "Portfolio" && (
              <div className="league-portfolio-container">
                <h2>Portfolio</h2>

                {/* Filter Buttons */}
                <div className="filter-container">
                  <span className="filter-label">Filter:</span>
                  <div className="filter-buttons">
                    <button
                      className={`filter-button ${
                        selectedPosition === "QB"
                          ? `pos-active ${positionClass("QB")}`
                          : ""
                      }`}
                      onClick={() =>
                        setSelectedPosition((prev) =>
                          prev === "QB" ? null : "QB"
                        )
                      }
                    >
                      QB
                    </button>
                    <button
                      className={`filter-button ${
                        selectedPosition === "RB"
                          ? `pos-active ${positionClass("RB")}`
                          : ""
                      }`}
                      onClick={() =>
                        setSelectedPosition((prev) =>
                          prev === "RB" ? null : "RB"
                        )
                      }
                    >
                      RB
                    </button>
                    <button
                      className={`filter-button ${
                        selectedPosition === "WR"
                          ? `pos-active ${positionClass("WR")}`
                          : ""
                      }`}
                      onClick={() =>
                        setSelectedPosition((prev) =>
                          prev === "WR" ? null : "WR"
                        )
                      }
                    >
                      WR
                    </button>
                    <button
                      className={`filter-button ${
                        selectedPosition === "TE"
                          ? `pos-active ${positionClass("TE")}`
                          : ""
                      }`}
                      onClick={() =>
                        setSelectedPosition((prev) =>
                          prev === "TE" ? null : "TE"
                        )
                      }
                    >
                      TE
                    </button>
                  </div>
                </div>

                {/* Portfolio Table */}
                <div className="league-portfolio-grid">
                  <div className="league-portfolio-grid-header">
                    Player Name
                  </div>
                  <div className="league-portfolio-grid-header align_center">
                    Position
                  </div>
                  <div 
                    className="league-portfolio-grid-header align_center"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("count", "descending")}
                  >
                    Count {getSortIcon("count")}
                  </div>
                  <div 
                    className="league-portfolio-grid-header align_center"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("fpts_g", "descending")}
                  >
                    FPTS/G {getSortIcon("fpts_g")}
                  </div>

                  {(() => {
                    // Sort portfolio data
                    const sortedData = [...portfolioData]
                      .filter((player) =>
                        selectedPosition
                          ? player.position === selectedPosition
                          : true
                      )
                      .sort((a, b) => {
                        if (!sortConfig.key) return 0;

                        let aValue, bValue;

                        if (sortConfig.key === "count") {
                          aValue = a.count ?? -Infinity;
                          bValue = b.count ?? -Infinity;
                        } else if (sortConfig.key === "fpts_g") {
                          // Convert "N/A" to -Infinity for sorting
                          aValue = a.fpts_g === "N/A" ? -Infinity : parseFloat(a.fpts_g) ?? -Infinity;
                          bValue = b.fpts_g === "N/A" ? -Infinity : parseFloat(b.fpts_g) ?? -Infinity;
                        } else {
                          return 0;
                        }

                        if (sortConfig.direction === "ascending") {
                          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
                        } else {
                          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
                        }
                      });

                    return sortedData.map((player, index) => {
                      const playerId = player.name.replace(" ", "-"); // Use player name as the unique identifier
                      return (
                        <React.Fragment key={index}>
                          <div
                            className={`league-portfolio-grid-item ${
                              selectedPlayer === playerId
                                ? "selected-player"
                                : ""
                            }`}
                            onClick={() =>
                              handlePlayerClick({
                                first_name: player.name.split(" ")[0],
                                last_name: player.name.split(" ")[1],
                              })
                            }
                          >
                            {player.name}
                          </div>
                          <div className="league-portfolio-grid-item align_center">
                            <PositionPill position={player.position} />
                          </div>
                          <div className="league-portfolio-grid-item align_center">
                            <span className="count">{player.count}</span>{" "}
                            <span className="percentage">
                              ({player.percentage}%)
                            </span>
                          </div>
                          <div className="league-portfolio-grid-item align_center">
                            {player.fpts_g || "N/A"}
                          </div>
                        </React.Fragment>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
            {activeTab === "Waivers" && (() => {
              // Only this week's claims are worth scanning on arrival; older ones are
              // kept behind the Archive rather than dropped, since the GraphQL fetch
              // returns every leg.
              const periodStart = currentWaiverPeriodStart();
              const sections = leagues.map(league => {
                const txns = (waiverData[league.league_id] || []).filter(t => t.status !== "cancelled");
                return {
                  league,
                  current: txns.filter(t => waiverIsCurrent(t, periodStart)),
                  past: txns.filter(t => !waiverIsCurrent(t, periodStart)),
                };
              });
              const currentCount = sections.reduce((n, sec) => n + sec.current.length, 0);
              const archivedCount = sections.reduce((n, sec) => n + sec.past.length, 0);
              return (
                <div className="league-waivers-container">
                  <h2>
                    Waivers{currentWeek > 0 && <span className="waiver-week-note"> — week {currentWeek}</span>}
                    {currentWeek === 0 && <span className="waiver-offseason-note">(preseason)</span>}
                  </h2>
                  {waiverLoading ? (
                    <p>Loading...</p>
                  ) : (
                    <>
                      {currentCount === 0 && (
                        <p className="waiver-empty">No waiver claims this week.</p>
                      )}
                      {sections.map(({ league, current }) => current.length > 0 && (
                        <div key={league.league_id} className="waiver-league-section">
                          <h3>{league.name}</h3>
                          <WaiverRows txns={current} names={waiverPlayerNames} />
                        </div>
                      ))}
                      {archivedCount > 0 && (
                        <div className="waiver-archive">
                          <button
                            className="waiver-archive-toggle"
                            onClick={() => setWaiverArchiveOpen(open => !open)}
                            aria-expanded={waiverArchiveOpen}
                          >
                            <FontAwesomeIcon
                              className="league-expander"
                              icon={waiverArchiveOpen ? faChevronDown : faChevronRight}
                            />{" "}
                            Archive ({archivedCount})
                          </button>
                          {waiverArchiveOpen && sections.map(({ league, past }) => past.length > 0 && (
                            <div key={league.league_id} className="waiver-league-section">
                              <h3>{league.name}</h3>
                              <WaiverRows txns={past} names={waiverPlayerNames} />
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
      <span hidden>{userId}</span>
    </div>
  );
}

export default LeagueList;
